import { ChangeDetectorRef, Component, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, map, tap } from "rxjs/operators";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { GenericTableAnagraphAnagraph } from "src/model/class/general classes/generic-table-anagraph-anagraph";
import { ReportHTML } from "src/model/class/general classes/report-html";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { NetworkService } from "src/service/network.service";
import { ReporthtmlService } from "src/service/reporthtml.service";
import { TranslationService } from "src/service/translation.service";
import { Editor, TinyMCE } from "tinymce";

declare var tinymce: TinyMCE;

@Component({
    selector: "app-reporthtml-form",
    templateUrl: "./reporthtml-form.component.html",
    styleUrls: ["./reporthtml-form.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
/**
 *
 *
 * This component manage simple anagraph report html editing
 * The saving process is manage on update single field
 * 
 * 
 * REFERENCES AND DOCUMENTATION FOR "tinyMCE" PLUGIN (HTML EDITOR)
 * 
 *Integrazione con Angular (IE. usare il plugin tramite un Compoente angualr "<Editor>" 
  https://www.tiny.cloud/docs/tinymce/6/angular-ref
	
  Eventi nativi di TinyMCE (per vedere quali sono supportati in Angular (tramite Angular event-hooks), guardare i doc. su integrazione)
  https://www.tiny.cloud/docs/advanced/events/
	
  Propietà e metodi della classe "core" di tinyMCE (creazione Editor, selezione editor tra molti, inizializzazione di Editor...)
  https://www.tiny.cloud/docs/api/tinymce/root_tinymce/
	
  Propietà e metodi della classe "Editor" di tinyMCE (Settare e recuperare dati contenuti manualmente (IE. non tramite Angular "Form") ....)
  https://www.tiny.cloud/docs/api/tinymce/tinymce.editor
 *
 */
export class ReporthtmlFormComponent
  extends GenericTableAnagraphAnagraph
  implements OnInit {
  private _networkServiceSubscription: Subscription;

  reportHTMLForm: UntypedFormGroup;
  tinyMCEConfiguration;
  tinymce: any;

  currentReportHTMLForm = new ReportHTML();
  /**This flag is used to block the reaction to the next emission of the "onDataChanged" form-event; after this emission has happened,
   * the flag is automatically reset to default value ("false").
   */
  private _blockFormChangeReaction: boolean = false;
  private _stop: boolean = false;
  private _isEditorInit: boolean = false;

  public showEditor: boolean = false;

  public isOnline: boolean = false;
  public editorSet: boolean = false;
  compareWith: (o1: any, o2: any) => boolean;


  @ViewChild("editor", { static: false })
  _editorRef: Editor;


  /*
  #environment_description
        ///     #environment_nroom
        ///     #start_date
        ///     #end_date
        ///     #total_amount
        ///     
        ///     #nd_description
        ///     #nd_start_value_datetime"
        ///     #nd_end_value_datetime
        ///     #nd_end_value
        ///     #nd_value
        ///     #nd_start_value
        ///     #nd_thousandths
        ///     #nd_percentage
        ///     #nd_unit_price
        ///     #nd_amount
        ///     #nd_discount1
        ///     #nd_discount2
        ///     #nd_discount3
        ///     #nd_discount4
   */
  constructor(
    private fb: UntypedFormBuilder,
    private reportHTMLService: ReporthtmlService,
    private _networkService: NetworkService,
    private _translationService: TranslationService,
    private _translateService: TranslateService,
    private _dc: ChangeDetectorRef,
  ) {
    super();
    this.compareWith = this.compareWithFn;

  }

  ngOnInit() {
    this.subscribeNetowrkServiceData();

    this.setEMCEditorConfiguration();

    this.initializeForm();

    /*
    ATTENTION: This timer is CRUCIAL to correct initialisation of sub-component "Editor"; it delays its renderization 
    by setting of a flag (controlled by a ngIf) only 1 s AFTER the display of the rest of the "Reporthtml-form.component" HTML template.

    Without this delay, it is possible that the Angular component associate with the "tinyMCE"-native Editor object is renderized BEFORE this 
    object is properly initialised/ready to be initialised; this causes it to break.
    */
    setTimeout(() => {
      this.showEditor = true;

      //ATTENTION
      //We moved all the code needed for the initialisation of the "Editor" object (native to tinyMCE), together with the initialisation of the other
      //more traditional fields of the Form, in the Handler for the tinyMCE-native "onInit" event.

    }, 1000);


  }

  ngOnDestroy() {
    this._isEditorInit = false;
    this._blockFormChangeReaction = true;

    this._networkServiceSubscription?.unsubscribe();
  }


  /**
   * Called by parent class ("structure-table-anagraph.component") when the current object is changed. This is used to provide to the complete Form the data to be displayed.
   * 
   * ATTENTION: This applies ONLY if the component (and the inner Editor) has already been initialised; otherwise, responsability to provide
   * the data falls upon the "ngOnInit" itself when triggered.
   */
  OnChanges() {
    if (this.CurrentObject) {
      if (this._isEditorInit) {
        this.updateFormValues();
      }
    }
  }

  /**
   * This method is the Handler of the tinyMCE-native "onInit" Event. It is launched when the tinyMCE-native Object "Editor" has been properly
   * created and initialised, being therefore ready to received a "content" to be displayed. This is done by directly acceding the native
   * Object and setting the extrapolated content.
   * 
   * We also take the occasion to initialise the other, more conventional fields of the Form.
   * @param event 
   */
  public onEditorInit(event): void {
    this._isEditorInit = true;

    if (this.CurrentObject) {
      this._blockFormChangeReaction = true;

      this.reportHTMLForm.patchValue(
        {
          description: this.CurrentObject.description,
          kind: this.CurrentObject.kind,
        },
        {
          onlySelf: true,
          emitEvent: false,
        });

      const CurrentObjectHTML: string = this.CurrentObject.html;

      const editorContent: string = this.prepareEditorContent(CurrentObjectHTML);

      if (tinymce.activeEditor) {

        tinymce.activeEditor.setContent(editorContent);
      }
    }
  }

  /**
   * This method is the Handler of the Browser "OnFocusOut" Event. It is used to avoid that the shift of the active focus away from the 
   * Editor object cause a reaction to a possible "dataChanges" event emission.
   * @param event 
   */
  public editorOnFocusOut(event): void {
    this._blockFormChangeReaction = true;
  }

  /**
   * This method is the Handler of the Browser "onFocusIn" Event. It is used to reset the  behaviuor of the "dataChages" event Handler
   * to normal.
   * @param event 
   */
  public editoronFocusIn(event): void {
    this._blockFormChangeReaction = false;
  }


  /**
   * This method ONLY initializes the "template" of the form. The effective data shall be passed through an external invocation of the 
   * "OnChanges()" method.
   */
  private initializeForm() {

    //If the "CurrentObject" is defined, the initialising values of the Form shall be directly taken from it. OtherWise, fallback values shall be used.
    let d = this.CurrentObject?.description;
    let k = this.CurrentObject?.kind;
    let h = this.CurrentObject?.html;

    const descr: string = (d ??= "");
    const kind = (k ??= 0);
    const html: string = (h ??= "");

    this.reportHTMLForm = this.fb.group({
      description: new UntypedFormControl(descr, [Validators.required]),
      kind: new UntypedFormControl(kind),
      html: new UntypedFormControl(html),
    });


    /**
     * On form field update, i wait if there aren't any emission for about 600ms
     * then i save new schedule data via API. valueChanges form field return an Observable object
     * that contain changed field: width distinctUntilChanged i discard same emission and debounceTime prevent
     * too fast form update.
     */
    this.reportHTMLForm.valueChanges
      .pipe(
        map((changedFormValue) => {
          this._stop = false;
          return changedFormValue;
        }),
        distinctUntilChanged(),
        debounceTime(AppSettings.DEFAULT_FORM_DEBOUNCE_TIME),
        map((changedFormValue) => {
          this._stop = this._blockFormChangeReaction;
          this._blockFormChangeReaction = false;
          return changedFormValue;
        }),
        filter(
          (changedFormValue) =>
            !this._stop
        ),
      )
      .subscribe((changedFormValue) => {

        this.onSave();

      });
  }


  private updateFormValues(): void {

    this.reportHTMLForm.patchValue(
      {
        description: this.CurrentObject.description,
        kind: this.CurrentObject.kind,
        html: this.CurrentObject.html
      },
      {
        onlySelf: true,
        emitEvent: false,
      });


    const CurrentObjectHTML: string = this.CurrentObject.html;

    let editorContent: string = this.prepareEditorContent(CurrentObjectHTML);

    if (tinymce.activeEditor) {
      tinymce.activeEditor.setContent(editorContent);

      this.refreshDOM();
    }


  }

  /**
   * This method produce a string to be used as "content" of the Editor object, to be passed directly to it through the correct tinyMCE native method
   * @param HTMLStr The original string; it should encapsulate the "content" between two "<BODY>"/"</BODY>" HTML tags. Otherwise, it is assumed it is equal to the content itself.
   * @returns 
   */
  private prepareEditorContent(HTMLStr: string): string {
    let editorContent = "";

    if (HTMLStr.indexOf("<BODY>") >= 0) {
      editorContent = ((HTMLStr.split("<BODY>")[1]).split("</BODY>"))[0];
    } else {
      editorContent = HTMLStr;
    }

    /*
    const index1: number = HTMLStr.indexOf("<table");
    const index2: number = HTMLStr.indexOf("</table>") + 7;
    editorContent = HTMLStr.substring(index1, index2);
*/
    return editorContent;
  }


  /**
   * Save new schedule form calling api
   *
   */
  private async onSave() {
    if (this.CurrentObject) {
      const report: ReportHTML = this.reportHTMLForm.value;

      //For safety reason, we reallign immediately the local "CurrentObject" with the changes just applied to the Form. 
      this.CurrentObject.description = report.description;
      this.CurrentObject.kind = report.kind;
      this.CurrentObject.html = report.html;

      if (this.CurrentObject.id === 0) {
        const newReport: ReportHTML =
          await this.reportHTMLService.InsertReportHTML(report);

        this.CurrentObject.id = newReport.id;
      } else {
        const updateSchedule: ReportHTML =
          await this.reportHTMLService.UpdateReportHTML(
            this.CurrentObject.id,
            report
          );
      }
    }
  }

  /**
   * Subscribe to network service observable data
   */
  private subscribeNetowrkServiceData(): void {
    this._networkServiceSubscription = this._networkService.currentConnectionType.subscribe(
      (currentConnectionType: ConnectionType) => {
        this.isOnline = currentConnectionType !== ConnectionType.NONE;
      }
    );
  }


  /**
 * This method forces Angular to check if there has been a recent chnage in page-backing data.
 *
 * ATTENTION: This should be used ONLY when a immediate data-binding-reaction is needed (EX: when DOM should change just before the page is left.)
 */
  private refreshDOM(): void {
    this._dc.detectChanges();
  }


  /**
   * This method set the configuration object which is later passed to the "Editor" component (through one specific @Input() property) in order to initialise it.
   */
  private setEMCEditorConfiguration(): void {

    this.tinyMCEConfiguration = {

      language: this._translationService.getTinymceLocalisationDir(),
      base_url: "/tinymce",
      suffix: ".min",
      plugins: [
        "advlist autolink lists link image charmap",
        "searchreplace visualblocks",
        "insertdatetime media table paste pagebreak help",
      ],
      toolbar:
        "undo redo | formatselect | " +
        "bold italic backcolor | alignleft aligncenter " +
        "alignright alignjustify | bullist numlist outdent indent | " +
        "removeformat | table tableprops tablerowprops tablecellprops | help ",
      help_tabs: [
        "shortcuts", // the default shortcuts tab
        {
          name: "keyword",
          title: "Keywords",
          items: [
            {
              type: "htmlpanel",
              html:
                "<h1>Keyword that can be used during the build of the template</h1>" +
                "<h2>Global for the payment entity</h2>" +
                "<p>#environment_description: description of the payment entity</p>" +
                "<p>#start_date: start date of the accounting</p>" +
                "<p>#end_date: end date of the accounting</p>" +
                "<p>#total_amount: total amount for the payment entity</p>" +
                "<h2>Specific for each counter</h2>" +
                "<p>#nd_description: description of the counter</p>" +
                "<p>#nd_start_value_datetime: timestamp of the first detection</p>" +
                "<p>#nd_start_value: value of the first detection</p>" +
                "<p>#nd_end_value_datetime: timestamp of the last detection</p>" +
                "<p>#nd_end_value: value of the last detection</p>" +
                "<p>#nd_value: counter value in the indicated period</p>" +
                "<p>#nd_thousandths: thousandths</p>" +
                "<p>#nd_percentage: percentage</p>" +
                "<p>#nd_unit_price: unit price</p>" +
                "<p>#nd_discount1: first discount appliable</p>" +
                "<p>#nd_discount2: second discount appliable</p>" +
                "<p>#nd_discount3: third discount appliable</p>" +
                "<p>#nd_discount4: firth discount appliable</p>" +
                "<p>#nd_amount: amount of the counter</p>",
            },
          ],
        },
      ],
      table_toolbar:
        "tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol",
      // Disable branding message, remove "Powered by TinyMCE"
      branding: false,
      statusbar: false,
      /* enable automatic uploads of images represented by blob or data URIs*/
      automatic_uploads: true,
      // A4 FORMAT
      height: 1360,
      width: 961,

      /*        
        here we add custom filepicker only to Image dialog
      */
      file_picker_types: "image",
      /* and here's our custom image picker*/
      file_picker_callback: function (cb, value, meta) {

        var input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");

        /*
          Note: In modern browsers input[type="file"] is functional without
          even adding it to the DOM, but that might not be the case in some older
          or quirky browsers like IE, so you might want to add it to the DOM
          just in case, and visually hide it. And do not forget do remove it
          once you do not need it anymore.
        */

        input.onchange = function () {

          var file = (<HTMLInputElement>this).files[0];

          var reader = new FileReader();
          reader.onload = function () {
            /*
              Note: Now we need to register the blob in TinyMCEs image blob
              registry. In the next release this part hopefully won't be
              necessary, as we are looking to handle it internally.
            */

            var id = "blobid" + new Date().getTime();
            var blobCache = tinymce.activeEditor.editorUpload.blobCache;
            var base64 = (reader.result as string).split(",")[1];
            var blobInfo = blobCache.create(id, file, base64);
            blobCache.add(blobInfo);

            /* call the callback and populate the Title field with the file name */
            cb(blobInfo.blobUri(), { title: file.name });
          };
          reader.readAsDataURL(file);
        };

        input.click();
      },
    };

  }



  public compareWithFn = (o1, o2) => {
    const res: boolean = parseInt(o1) === parseInt(o2);
    return parseInt(o1) === parseInt(o2);
  };
}
